import React, {useContext, useEffect, useState} from 'react';
import {Modal} from "react-bootstrap";
import IframeWidget from "../../../../utils/IframeWidget";
import StoreUtils from "../../../../services/Store/StoreUtils";
import {useLocation, useNavigate} from "react-router-dom";
import {usePrefix} from "../../../../services/Prefix";
import {ConfigApi} from "../../../../services/Api/types";
import {VoucherProductsContext} from "../../../../services/VoucherProducts/VoucherProductsContext";
import MenuProductModalHeader from "./MenuProductModalHeader";
import ResponsiveComponent from "../../../../components/Common/ResponsiveComponent";
import MenuProductImages from "./MenuProductImages";
import StateItemHelper from "../../../../services/Menu/services/State/StateItemHelper";
import {RootState} from "../../../../../reducers";
import {
    getAvailabilities,
    getCategories,
    getItemGroups,
    getItems,
    getModifierGroups,
    getPromotions
} from "../../../../services/Menu/reducer";
import {connect} from "react-redux";
import {getSession} from "../../../../services/Session/reducer";
import PromotionHelper from "../../../../services/Menu/services/Promotion/PromotionHelper";
import {getCart} from "../../../../services/Cart/reducer";
import ProductContent from "./ProductContent/ProductContent";
import MenuEditItemViewHelper from "../../../../services/Menu/services/ItemView/MenuEditItemViewHelper";
import MenuProductModalFooter from "./MenuProductModalFooter";
import handleException from "../../../../services/Api/handleException";
import {useCart} from "../../../../services/Cart/cartService";
import AppConfig from "../../../../services/AppConfig";
import ItemHelper from "../../../../services/Menu/services/Item/ItemHelper";
import DateService from "../../../../services/Common/Date/DateService";
import AgeRestrictedContext from "../../../../services/Common/AgeRestrictedContext";
import AgeRestrictedModalComponent from "../AgeRestricted/AgeRestrictedModalComponent";

interface OwnProps {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    config: ConfigApi | undefined;
    item: Record<string, any>;
    itemGroup: Record<string, any> | undefined;
    nested?: true;
    customOnSubmit?: (state: Record<string, any>) => void;
    externalItemState?: Record<string, any>;
    preventRemovePromotionOnHide?: boolean;
    onHide?: (itemState?: Record<string, any>) => void;
    orderItemId?: number;
    preventGoBack?: true;
    menuId?: number;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const MenuProductModal = ({
                              show,
                              setShow,
                              config,
                              item,
                              itemGroup,
                              items,
                              modifierGroups,
                              session,
                              promotions,
                              cart,
                              nested,
                              customOnSubmit,
                              externalItemState,
                              availabilities,
                              preventRemovePromotionOnHide,
                              onHide,
                              orderItemId,
                              preventGoBack,
                              categories,
                              menuId
                          }: Props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const prefix = usePrefix();
    const {handleOnPromotionLimit, handleRemovePromotion} = useContext(VoucherProductsContext);
    const isReadonly = StoreUtils.isOrderingReadonly(config);
    const [itemState, setItemState] = useState<Record<string, any>>(externalItemState ?? StateItemHelper.create(item.id, items, modifierGroups, session.order_type));
    const productView = MenuEditItemViewHelper.getItemView(items, modifierGroups, availabilities, itemState.item_id, undefined, undefined, 1, session.pickup_at, itemState, session.order_type, isReadonly);
    const [isItemAvailable, setIsItemAvailable] = useState(true);
    const [isItemDisabled, setIsItemDisabled] = useState(false);
    const [errors, setErrors] = useState<any[]>([]);
    const cartService = useCart();
    const [loading, setLoading] = useState(false);
    const parsedItemGroup = itemGroup ? ItemHelper.filterItemGroupItemsByMenuUsage(itemGroup, categories) : undefined;
    const [hasNoAvailableInfo, setHasNoAvailableInfo] = useState(false);
    const isMultilevelMenu = StoreUtils.isMultilevelMenu(config);
    const isAgeConfirmed = AgeRestrictedContext.isAccepted(StoreUtils.isAgeRestrictionDisabled(config));
    const [showAgeRestricted, setShowAgeRestricted] = useState(item.energy_drink && !isAgeConfirmed && AgeRestrictedContext.alreadyChecked());
    const [itemRestricted, setItemRestricted] = useState(item.energy_drink && !isAgeConfirmed);

    useEffect(() => {
        if (item.alcohol && !isAgeConfirmed) {
            handleHideModal();
        }

        return () => {
            IframeWidget.closeModal();
        }
    }, []);

    useEffect(() => {
        handleIsItemDisabled()
    }, [isItemAvailable, hasNoAvailableInfo]);

    const handleIsItemDisabled = () => {
        if (isReadonly) {
            const isSuspended = !ItemHelper.isAvailabile(item, availabilities, DateService.emptyOrNow(undefined), undefined, {
                skipAvailability: true,
                skipStatus: true,
                skipType: true
            });
            const isEnabled = item.status === "ENABLED";
            setIsItemDisabled(isSuspended || !isEnabled || hasNoAvailableInfo)
        } else {
            setIsItemDisabled(!isItemAvailable);
        }
    }

    const getGoBackUrl = () => {
        const pathname = location.pathname;

        if (pathname.includes("cart")) {
            return "/cart";
        }

        if (StoreUtils.isHomepageMenu(config)) {
            return isMultilevelMenu && menuId ? `/menu/${menuId}` : "/"
        }

        return isMultilevelMenu && menuId ? `/menu/${menuId}` : `/menu`;
    }

    const handleNavigateFromModal = () => {
        if (preventGoBack) return;

        const backUrl = getGoBackUrl();

        if (location.state && location.state.categoryPageId) {
            const link = `${backUrl}/category/${location.state.categoryPageId}`;
            navigate(AppConfig.getLinkWithPrefix(link, prefix), {
                replace: true,
                state: {action: 'stay'}
            });
        } else if (location.state && location.state?.back) {
            if (IframeWidget.isInit()) {
                navigate(backUrl);
            } else {
                navigate(-1);
            }
        } else {
            navigate(AppConfig.getLinkWithPrefix(backUrl, prefix), {
                replace: true,
                state: {action: 'stay'}
            });
        }
    };

    const handleAddProductToCart = async () => {
        setLoading(true);
        setErrors([]);

        try {
            if (orderItemId) {
                itemState.id = orderItemId;
            }
            await cartService.addItem(itemState);

            const isItemInCart = itemState?.item_id === item.id;

            if (location.state?.promotionId && !isItemInCart && !preventRemovePromotionOnHide) {
                await handleRemovePromotion();
            }

            if (location.state?.fromVoucher) {
                handleOnPromotionLimit(itemState);
            }

            setShow(false);
            handleNavigateFromModal();
        } catch (resError: any) {
            setErrors(handleException(resError));
        }
        setLoading(false);
    };

    const handleSubmit = async () => {
        if (nested) {
            setShow(false);
            customOnSubmit?.(itemState);
        } else {
            await handleAddProductToCart();
        }
    }

    const handleHideModal = async () => {
        onHide?.(itemState);

        if (nested) {
            setShow(false);
            return;
        }

        if (location.state?.fromVoucher) {
            setShow(false);
            handleNavigateFromModal();
        } else {
            setShow(false);

            if (location.state?.promotionId) {
                await handleRemovePromotion();
            }

            handleNavigateFromModal();
        }
    }

    const getModalTitle = () => {
        if (parsedItemGroup) return parsedItemGroup.translation ? parsedItemGroup.translation.name : parsedItemGroup.name;

        return item.translation.name;
    }

    const isSimpleView = () => {
        return !productView.description && !item.modifier_groups?.length && !item.selected_items?.length && !StoreUtils.isItemCommentAvailable(config) && !productView.content;
    };

    const handleUpdateItemState = (newItemState: Record<string, any>) => {
        const copiedItemState = JSON.parse(JSON.stringify(newItemState));
        StateItemHelper.refreshItemPrice(copiedItemState, PromotionHelper.getForItem(promotions, newItemState.item_id), cart);
        setItemState(copiedItemState);
    };

    return (
        <Modal dialogClassName={`modal-full product-modal ${isSimpleView() ? "product-modal--small" : ""}`}
               show={show}
               aria-labelledby="menu_product_modal" id="product-modal"
               onHide={handleHideModal}
               onShow={() => IframeWidget.onShow()}>
            {showAgeRestricted && <AgeRestrictedModalComponent externalShow={showAgeRestricted} zIndex={1100}
                                                               onConfirm={() => setItemRestricted(false)}
                                                               onReject={() => setItemRestricted(true)}
                                                               externalOnHide={() => setShowAgeRestricted(false)}/>}
            <ResponsiveComponent small={
                <>
                    <MenuProductModalHeader onClick={handleHideModal} title={getModalTitle()}
                                            ariaLabelledBy="menu_product_modal"/>
                    <Modal.Body>
                        <ProductContent productState={itemState} product={item} displayImages
                                        productItemGroup={parsedItemGroup}
                                        updateProductState={handleUpdateItemState}
                                        menuId={menuId}
                                        productView={productView} nested={nested} isProductAvailable={!isItemDisabled}/>
                    </Modal.Body>
                    <MenuProductModalFooter productState={itemState} isProductAvailable={isItemAvailable}
                                            simple={nested} onSubmit={handleSubmit}
                                            productView={productView} product={item}
                                            setIsProductAvailable={setIsItemAvailable}
                                            updateProductState={handleUpdateItemState}
                                            errors={errors} loading={loading} productItemGroup={parsedItemGroup}
                                            config={config} setHasNoAvailableInfo={setHasNoAvailableInfo}
                                            menuId={menuId} productRestricted={itemRestricted}
                    />
                </>
            } large={
                isSimpleView() ? (
                    <>
                        <MenuProductModalHeader onClick={handleHideModal} title={getModalTitle()}
                                                ariaLabelledBy="menu_product_modal"/>
                        <Modal.Body>
                            <ProductContent productState={itemState} product={item} displayImages
                                            productItemGroup={parsedItemGroup} isProductAvailable={!isItemDisabled}
                                            updateProductState={handleUpdateItemState}
                                            productView={productView} nested={nested} menuId={menuId}/>
                        </Modal.Body>
                        <MenuProductModalFooter productState={itemState} isProductAvailable={isItemAvailable}
                                                simple={nested} onSubmit={handleSubmit}
                                                productView={productView} product={item}
                                                setIsProductAvailable={setIsItemAvailable}
                                                updateProductState={handleUpdateItemState}
                                                errors={errors} loading={loading} productItemGroup={parsedItemGroup}
                                                config={config} setHasNoAvailableInfo={setHasNoAvailableInfo}
                                                menuId={menuId} productRestricted={itemRestricted}
                        />
                    </>
                ) : (
                    <Modal.Body>
                        <div className="columns-container">
                            <div className="col-6 col-left">
                                <MenuProductImages product={item} isProductAvailable={!isItemDisabled}/>
                            </div>
                            <div className="col-6 col-right">
                                <MenuProductModalHeader onClick={handleHideModal}
                                                        title={getModalTitle()}
                                                        ariaLabelledBy="menu_product_modal"/>
                                <ProductContent productState={itemState} productItemGroup={parsedItemGroup}
                                                product={item} menuId={menuId}
                                                updateProductState={handleUpdateItemState} productView={productView}
                                                nested={nested} isProductAvailable={!isItemDisabled}/>
                                <MenuProductModalFooter productState={itemState} isProductAvailable={isItemAvailable}
                                                        simple={nested} onSubmit={handleSubmit}
                                                        productView={productView} product={item}
                                                        setIsProductAvailable={setIsItemAvailable}
                                                        updateProductState={handleUpdateItemState}
                                                        errors={errors} loading={loading}
                                                        productItemGroup={parsedItemGroup}
                                                        config={config} setHasNoAvailableInfo={setHasNoAvailableInfo}
                                                        menuId={menuId} productRestricted={itemRestricted}
                                />
                            </div>
                        </div>
                    </Modal.Body>
                )
            } breakpoint={832}/>
        </Modal>
    )
}

const mapStateToProps = (state: RootState) => ({
    items: getItems(state.menu),
    session: getSession(state.session),
    itemGroups: getItemGroups(state.menu),
    modifierGroups: getModifierGroups(state.menu),
    promotions: getPromotions(state.menu),
    cart: getCart(state.cart),
    availabilities: getAvailabilities(state.menu),
    categories: getCategories(state.menu),
})

const connector = connect(mapStateToProps);
export default connector(MenuProductModal);
